import { TextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

import { BookData } from '../interfaces/book-data.interface';
import { findEmptyTextFields, type EmptyTextFieldRef } from './empty-text-fields';

/**
 * Mirrors `PERSON_TEXT_TYPES` (dto-fotoclass-book). The set itself cannot be imported here:
 * dto-fotoclass-book already depends on photobook-model, the import would close a cycle.
 */
const PERSON_OWNED_TEXT_TYPES: ReadonlySet<string> = new Set<string>([
  TextTypeEnum.PERSON_FIRST_NAME,
  TextTypeEnum.PERSON_LAST_NAME,
  TextTypeEnum.PERSON_FULL_NAME,
  TextTypeEnum.PERSON_PATRONYMIC,
  TextTypeEnum.PERSON_GIVEN_PATRONYMIC,
  TextTypeEnum.PERSON_QUOTE,
  TextTypeEnum.PERSON_SUBJECT,
]);

/**
 * «Обложка мастер-альбома ещё не заполнена» — вердикт гейта ссылок и гейта сборки. Судит ЛЮБОЙ видимый
 * текст обложки, а не только размеченные слоты проекта: пустая авторская надпись уйдёт в печать всему
 * классу ровно так же, как пустой «класс».
 *
 * Слоты персоны не в счёт: у мастера нет владельца, они заполняются у каждого ученика при сборке — в
 * сохранённой модели они пусты по устройству.
 *
 * Книга без обложки — не ответ «пусто»: тот же вердикт, что `missingCoverTextTypes` даёт такой книге.
 * Нечитаемую модель гейты закрывают у себя, до этой проверки.
 */
export function hasBlankCoverTexts(book: Pick<BookData, 'cover'> | null | undefined): boolean {
  return blankCoverTextFields(book).length > 0;
}

/**
 * Те же поля, но СПИСКОМ — для гейта утверждения и подсветки: он называет и обводит ровно те поля,
 * по которым `hasBlankCoverTexts` запирает ссылки и сборку, а не пересчитывает обложку своим законом.
 */
export function blankCoverTextFields(book: Pick<BookData, 'cover'> | null | undefined): readonly EmptyTextFieldRef[] {
  if (!book?.cover) return [];

  // The walk of `findEmptyTextFields` is exactly the one the gates need — visibility, group recursion and
  // whitespace-only all decided there. Spreads are cut away so every hit belongs to the cover.
  return findEmptyTextFields({ cover: book.cover, spreads: [] }).filter(
    field => !PERSON_OWNED_TEXT_TYPES.has(field.textType ?? ''),
  );
}
